/**
 * @module FactPackPanel
 * @purpose L2 fact pack — composes the structured B4 fact groups (counts, process references,
 *   temporal, hashes + LOLBAS), each rendered parse-free from the cluster detail with the
 *   adjacent Part C observation list (OQ5 grouped fact↔observation). Pure / prop-driven.
 * @depends FactPackPanel.parts, types/reasoning
 * @dependedOnBy ReasoningClusterCard (L2 drill-down region)
 *
 * @rules
 * - Performance: pure display — all data via props; the only client derivation is the
 *   hash_summary count (NFR1-permitted), done inside CountsGroup
 * - State: none
 * - Errors: n/a — purely presentational; tolerates a null briefing (B4 facts still render)
 */

import type { ClusterDetail, ClusterBriefing, HashResolution } from '../../../../../types/reasoning'
import { CountsGroup, ReferencesGroup, TemporalGroup, HashesGroup } from './FactPackPanel.parts'

interface FactPackPanelProps {
  detail: ClusterDetail
  briefing: ClusterBriefing | null
  onResolveHash?: (resolution: HashResolution) => Promise<void>
}

/**
 * Renders the L2 fact pack from B4 structured fields, never parsing the rendered `*_text`.
 *
 * @behaviour Stacks the four fact groups; each pairs the structured fact with the matching
 *   Part C observation list. A null briefing simply omits the observation lists — the B4
 *   facts always render.
 * @errors n/a — purely presentational
 */
export function FactPackPanel({ detail, briefing, onResolveHash }: FactPackPanelProps): React.ReactElement {
  return (
    <div style={{ marginTop: '8px' }}>
      <CountsGroup detail={detail} briefing={briefing} />
      <ReferencesGroup referenceSummary={detail.referenceSummary} />
      <TemporalGroup detail={detail} briefing={briefing} />
      <HashesGroup
        detail={detail}
        briefing={briefing}
        {...(onResolveHash !== undefined ? { onResolveHash } : {})}
      />
    </div>
  )
}
